import { Save, Trash2, Search, Edit2, Plus, Info, Loader2 } from "lucide-react";
import { EditMembershipSkuDrawer } from "@/modules/hub/features/MembershipSkuManagement/components/EditMembershipSkuDrawer/EditMembershipSkuDrawer";
import { DeleteConfirmDialog } from "@/modules/hub/features/MembershipSkuManagement/components/DeleteConfirmDialog/DeleteConfirmDialog";
import { useMembershipSkuSection } from "@/modules/hub/features/MembershipSkuManagement/hooks/useMembershipSkuSection";

interface MembershipSkuSectionProps {
  currentTenant?: string;
}

const getValidityPeriod = (duration: string) =>
  duration === "annual" ? "12 months" : "30 days";

const getMembershipTypeLabel = (duration: string) =>
  duration === "annual" ? "Annual" : "Monthly";

export const MembershipSkuSection = ({
  currentTenant,
}: MembershipSkuSectionProps) => {
  const {
    activeCount,
    closeEditDrawer,
    confirmDelete,
    description,
    filteredSkus,
    handleAddSku,
    handleEdit,
    handleSaveAllChanges,
    handleToggleStatus,
    hasChanges,
    isCreating,
    isEditOpen,
    isSaving,
    loading,
    membershipType,
    newSkuPattern,
    searchTerm,
    setDescription,
    setMembershipType,
    setNewSkuPattern,
    setSearchTerm,
    setSkuToDelete,
    skuToDelete,
    totalCount,
    validationError,
  } = useMembershipSkuSection(currentTenant);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading membership SKUs...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-6">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-green-500" />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {activeCount} Active SKUs
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-gray-400 dark:bg-slate-500" />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {totalCount} Total SKUs
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 p-4 dark:border-slate-700">
        <h4 className="mb-4 text-sm font-normal text-gray-900 dark:text-white">
          Add New Membership SKU
        </h4>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              SKU Code <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              onChange={(e) => setNewSkuPattern(e.target.value)}
              placeholder="e.g., CLUB-ANNUAL"
              type="text"
              value={newSkuPattern}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Membership Type
            </label>
            <select
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              onChange={(e) =>
                setMembershipType(e.target.value as "annual" | "monthly")
              }
              value={membershipType}
            >
              <option value="annual">Annual (12 months)</option>
              <option value="monthly">Monthly (30 days)</option>
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description
            </label>
            <input
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              type="text"
              value={description}
            />
          </div>

          {validationError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {validationError}
            </div>
          )}

          <button
            className="flex items-center gap-2 rounded-md border border-transparent bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isCreating}
            onClick={handleAddSku}
          >
            <Plus className="h-4 w-4" />
            {isCreating ? "Adding..." : "Add SKU"}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-slate-700">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
          <h4 className="text-sm font-normal text-gray-900 dark:text-white">
            Configured SKUs
          </h4>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 sm:w-56"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search SKUs..."
                type="text"
                value={searchTerm}
              />
            </div>
            <button
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!hasChanges || isSaving}
              onClick={handleSaveAllChanges}
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Saving..." : "Save All Changes"}
            </button>
          </div>
        </div>

        {filteredSkus.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
            {searchTerm
              ? "No SKUs found matching your search"
              : "No membership SKU patterns configured yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                    SKU Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                    Validity Period
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {filteredSkus.map((sku) => (
                  <tr
                    className="transition-colors hover:bg-gray-50 dark:hover:bg-slate-700/50"
                    key={sku.uuid}
                  >
                    <td className="px-4 py-3">
                      <button
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          sku.isActive
                            ? "bg-green-500"
                            : "bg-gray-300 dark:bg-slate-600"
                        }`}
                        onClick={() => handleToggleStatus(sku.uuid)}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            sku.isActive ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <code className="font-mono text-sm text-gray-900 dark:text-gray-100">
                        {sku.skuPattern}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-slate-700 dark:text-gray-300">
                        {getMembershipTypeLabel(sku.membershipDuration)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {sku.description || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {getValidityPeriod(sku.membershipDuration)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-slate-700 dark:hover:text-white"
                          onClick={() => handleEdit(sku)}
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          className="rounded p-1 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                          onClick={() => setSkuToDelete(sku)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex gap-3 rounded-lg border border-gray-200 p-4 dark:border-slate-700">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          When invoices are synced from QuickBooks, the system checks each line
          item against these SKUs. If a match is found, the customer is
          automatically tagged as a club member based on the membership type
          (annual = 12 months validity, monthly = 30 days validity from invoice
          date).
        </p>
      </div>

      <EditMembershipSkuDrawer
        currentTenant={currentTenant}
        onClose={closeEditDrawer}
        open={isEditOpen}
      />
      <DeleteConfirmDialog
        description={
          skuToDelete
            ? `Are you sure you want to delete the SKU pattern "${skuToDelete.skuPattern}"? This action cannot be undone.`
            : ""
        }
        onClose={() => setSkuToDelete(null)}
        onConfirm={confirmDelete}
        open={!!skuToDelete}
        title="Delete Membership SKU Pattern?"
      />
    </div>
  );
};
